import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { parentRegister } from '../../services/api';

// ─── Brand tokens (parent interface) ──────────────────────────────────────────

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  text: '#1E293B',
  textMuted: '#64748B',
  textSubtle: '#94A3B8',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentDarker: '#003D99',
  accentSoft: '#EFF4FF',
  accentBorder: '#C7D7FE',
  border: '#E2E8F0',
  green: '#10B981',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
  greenText: '#065F46',
  amber: '#F59E0B',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT = "'DM Sans', sans-serif";

// ─── Brand mark ───────────────────────────────────────────────────────────────

function LanternBrand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
      <div style={{
        width: 32, height: 32, borderRadius: 9,
        background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDarker} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 2px 8px ${C.accent}55`,
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="5" y="3" width="6" height="8" rx="1" stroke="#fff" strokeWidth="1.4"/>
          <path d="M6.5 3V2h3v1M5 11h6M7 13h2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{
        fontFamily: HEADING_FONT, fontSize: 18, fontWeight: 700,
        color: C.text, letterSpacing: '-0.01em',
      }}>Lantern</span>
    </div>
  );
}

// ─── Password strength ────────────────────────────────────────────────────────

function PasswordStrength({ password }) {
  if (!password) return null;
  const checks = [
    password.length >= 12,
    /[A-Z]/.test(password),
    /[a-z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const label = score === 5 ? 'Strong' : score >= 3 ? 'Good' : 'Weak';
  const color = score === 5 ? C.green : score >= 3 ? C.amber : C.red;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
      <span style={{ display: 'inline-flex', gap: 2 }}>
        {[0, 1, 2, 3, 4].map(i => (
          <span key={i} style={{
            width: 12, height: 4, borderRadius: 2,
            background: i < score ? color : C.border,
            transition: 'background 160ms ease',
          }} />
        ))}
      </span>
      <span style={{ fontSize: 11, color, fontWeight: 600 }}>{label}</span>
    </span>
  );
}

// ─── Consent definitions ──────────────────────────────────────────────────────
// Keys must match server schema in server/routes/auth.js → consentsSchema.

const CONSENTS = [
  {
    key: 'TERMS',
    label: 'I accept the Terms of Service and Privacy Policy.',
  },
  {
    key: 'CHILD_DATA_PROCESSING',
    label: 'As the parent or guardian, I confirm I am responsible for processing my child’s data through Lantern.',
  },
  {
    key: 'ACCOUNT_CREATION',
    label: 'I understand Lantern’s safeguarding system and how it protects my child during AI conversations.',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function friendlyError(err) {
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.error;
  if (status === 400 && serverMessage) return serverMessage;
  if (status === 429) return 'Too many sign-up attempts. Please wait an hour and try again.';
  return 'Something went wrong creating your account. Please try again in a moment.';
}

// ─── Main register ────────────────────────────────────────────────────────────

export default function ParentRegister() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [consents, setConsents] = useState({
    TERMS: false,
    CHILD_DATA_PROCESSING: false,
    ACCOUNT_CREATION: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [focused, setFocused] = useState(null);

  const allConsentsGiven = Object.values(consents).every(Boolean);
  const passwordsMatch = password === confirmPassword;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!email.trim()) return setError('Please enter your email.');
    if (!password) return setError('Please choose a password.');
    if (!passwordsMatch) return setError('Passwords don’t match.');
    if (!allConsentsGiven) return setError('Please accept all three consents to continue.');

    setSubmitting(true);
    try {
      await parentRegister(email.trim(), password, confirmPassword, consents);
      setSuccess(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const inputWith = (name) => ({
    border: `1px solid ${focused === name ? C.accent : C.border}`,
    boxShadow: focused === name ? `0 0 0 4px ${C.accent}22` : 'none',
    borderRadius: 10,
    padding: '12px 14px',
    fontFamily: BODY_FONT,
    fontSize: 15,
    color: C.text,
    outline: 'none',
    background: '#fff',
    width: '100%',
    transition: 'border-color 160ms ease, box-shadow 160ms ease',
  });

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: `${C.bg} radial-gradient(ellipse 600px 400px at 50% -10%, ${C.accent}1c 0%, ${C.accent}00 60%)`,
        fontFamily: BODY_FONT,
        color: C.text,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 20px 60px',
      }}>
        <div style={{ width: '100%', maxWidth: 460 }}>
          <div style={{ marginBottom: 24 }}>
            <LanternBrand />
          </div>
          <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 16,
            padding: '36px 28px',
            boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
            textAlign: 'center',
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: '#fff',
              border: `2px solid ${C.greenBorder}`,
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(16, 185, 129, 0.18)',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M5 12.5l4.5 4.5L19 7.5" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1 style={{
              margin: '0 0 8px',
              fontFamily: HEADING_FONT,
              fontSize: 24,
              fontWeight: 700,
              color: C.text,
              letterSpacing: '-0.02em',
            }}>Check your email</h1>
            <p style={{
              margin: '0 auto',
              fontSize: 14,
              color: C.textMuted,
              lineHeight: 1.55,
              maxWidth: 360,
            }}>
              We&rsquo;ve sent a verification link to <strong style={{ color: C.text }}>{email}</strong>. Click it to activate your account, then sign in.
            </p>
            <p style={{
              margin: '20px auto 0',
              fontSize: 13,
              color: C.textSubtle,
              lineHeight: 1.55,
              maxWidth: 360,
            }}>
              The link works for 24 hours. Don&rsquo;t see it? Check your spam folder.
            </p>
            <Link
              to="/parent/login"
              style={{
                display: 'inline-block',
                marginTop: 24,
                color: C.accent,
                fontWeight: 600,
                textDecoration: 'none',
                fontSize: 14,
              }}
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: `${C.bg} radial-gradient(ellipse 600px 400px at 50% -10%, ${C.accent}1c 0%, ${C.accent}00 60%)`,
      fontFamily: BODY_FONT,
      color: C.text,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '40px 20px 60px',
    }}>
      <div style={{ width: '100%', maxWidth: 460 }}>
        <div style={{ marginBottom: 24 }}>
          <LanternBrand />
        </div>

        <div style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: '32px 28px',
          boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
        }}>
          <h1 style={{
            margin: 0,
            fontFamily: HEADING_FONT,
            fontSize: 26,
            fontWeight: 700,
            color: C.text,
            letterSpacing: '-0.02em',
          }}>Create your account</h1>
          <p style={{ margin: '6px 0 24px', fontSize: 14, color: C.textMuted }}>
            One account per family — you&rsquo;ll add your children after this step.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <label htmlFor="parent-email" style={{
              display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: C.text,
            }}>Email</label>
            <input
              id="parent-email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              value={email}
              onChange={(e) => { setError(null); setEmail(e.target.value); }}
              onFocus={() => setFocused('email')}
              onBlur={() => setFocused(null)}
              placeholder="you@example.com"
              disabled={submitting}
              style={inputWith('email')}
            />

            {/* Password */}
            <label htmlFor="parent-password" style={{
              display: 'flex',
              alignItems: 'center',
              fontSize: 13,
              fontWeight: 500,
              margin: '18px 0 6px',
              color: C.text,
            }}>
              <span>Password</span>
              <PasswordStrength password={password} />
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="parent-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => { setError(null); setPassword(e.target.value); }}
                onFocus={() => setFocused('password')}
                onBlur={() => setFocused(null)}
                placeholder="At least 12 characters"
                disabled={submitting}
                style={{ ...inputWith('password'), paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute', right: 8, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none', padding: 6,
                  cursor: 'pointer', color: C.textMuted,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M3 3l14 14M8.5 4.6a8 8 0 0 1 1.5-.1c5 0 8 5.5 8 5.5a14 14 0 0 1-2.2 2.9M6.2 6.2A14 14 0 0 0 2 10s3 5.5 8 5.5c1.4 0 2.6-.3 3.7-.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                    <path d="M8.5 8.5a2 2 0 0 0 2.8 2.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M2 10s3-5.5 8-5.5 8 5.5 8 5.5-3 5.5-8 5.5S2 10 2 10z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                    <circle cx="10" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.6"/>
                  </svg>
                )}
              </button>
            </div>
            <p style={{ margin: '6px 4px 0', fontSize: 12, color: C.textSubtle }}>
              Must include uppercase, lowercase, a number, and a special character.
            </p>

            {/* Confirm password */}
            <label htmlFor="parent-confirm" style={{
              display: 'block', fontSize: 13, fontWeight: 500, margin: '18px 0 6px', color: C.text,
            }}>Confirm password</label>
            <input
              id="parent-confirm"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => { setError(null); setConfirmPassword(e.target.value); }}
              onFocus={() => setFocused('confirm')}
              onBlur={() => setFocused(null)}
              placeholder="Type it again"
              disabled={submitting}
              style={inputWith('confirm')}
            />
            {confirmPassword && !passwordsMatch && (
              <p style={{ margin: '6px 4px 0', fontSize: 12, color: C.redText, fontWeight: 500 }}>
                Passwords don&rsquo;t match yet.
              </p>
            )}

            {/* Consents */}
            <fieldset style={{
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              padding: '14px 16px 12px',
              margin: '24px 0 0',
              background: C.surfaceMuted,
            }}>
              <legend style={{
                padding: '0 6px',
                fontFamily: HEADING_FONT,
                fontSize: 13,
                fontWeight: 600,
                color: C.text,
                letterSpacing: '-0.005em',
              }}>Confirm you understand</legend>
              {CONSENTS.map(({ key, label }) => (
                <label
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    margin: '8px 0',
                    fontSize: 13,
                    color: C.text,
                    cursor: 'pointer',
                    lineHeight: 1.5,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={consents[key]}
                    onChange={(e) => {
                      setError(null);
                      setConsents(c => ({ ...c, [key]: e.target.checked }));
                    }}
                    disabled={submitting}
                    style={{
                      width: 18,
                      height: 18,
                      marginTop: 1,
                      accentColor: C.accent,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </fieldset>

            {error && (
              <div role="alert" style={{
                marginTop: 18,
                padding: '10px 14px',
                background: C.redSoft,
                border: `1px solid ${C.redBorder}`,
                borderRadius: 10,
                color: C.redText,
                fontSize: 13,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="8" cy="8" r="7" stroke={C.redText} strokeWidth="1.5"/>
                  <path d="M8 4v5M8 11.5v.5" stroke={C.redText} strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                marginTop: 22,
                width: '100%',
                minHeight: 48,
                padding: '14px 24px',
                background: C.accent,
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontFamily: BODY_FONT,
                fontSize: 15,
                fontWeight: 600,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
                boxShadow: '0 1px 0 rgba(0,61,153,0.4) inset, 0 1px 2px rgba(0,102,255,0.25), 0 4px 12px rgba(0,102,255,0.18)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {submitting ? (
                <>
                  <span style={{
                    width: 16, height: 16, borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: '#fff',
                    animation: 'pr-spin 700ms linear infinite',
                  }}/>
                  Creating account&hellip;
                </>
              ) : 'Create account'}
            </button>
          </form>

          <style>{`@keyframes pr-spin { to { transform: rotate(360deg); } }`}</style>
        </div>

        <p style={{
          marginTop: 18,
          textAlign: 'center',
          fontSize: 14,
          color: C.textMuted,
        }}>
          Already have an account?{' '}
          <Link to="/parent/login" style={{ color: C.accent, fontWeight: 600, textDecoration: 'none' }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
